import { useCallback, useEffect, useRef } from 'react';
import {
  tariffVerificationValuesSelector,
  TariffWithVerificationValues,
  updateTariffVerificationValues,
} from '@src/stores/product';
import { createOrUpdateVerificationValues } from '@src/domains/tariffs/core/tariffVerificationFields/createOrUpdateVerificationValues';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';

/**
 * Hook to manage tariff verification handlers
 */
export const useTariffVerificationHandlers = (tariffTypeId: string) => {
  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  const dispatch = useAppDispatch();

  const tariffWithVerificationValues = useAppSelector(tariffVerificationValuesSelector(widgetId, productId));

  // Use ref to store the most recent values to avoid closure issues
  const tariffWithVerificationValuesRef = useRef<TariffWithVerificationValues[]>([]);

  useEffect(() => {
    tariffWithVerificationValuesRef.current = tariffWithVerificationValues;
  }, [tariffWithVerificationValues]);

  const handleValueChange = useCallback(
    (verificationId: string, value: string, tariffUnitIndex: number, verificationPosition: number) => {
      let currentTariff = tariffWithVerificationValuesRef.current.find(
        (verification) => verification.tariffTypeId === tariffTypeId,
      );

      if (!currentTariff) {
        currentTariff = {
          tariffTypeId,
          verificationValues: [
            {
              tariffVerificationId: verificationId,
              tariffUnitIndex,
              verificationPosition,
              value,
            },
          ],
        };
      }

      currentTariff = createOrUpdateVerificationValues(
        currentTariff,
        verificationId,
        value,
        tariffUnitIndex,
        verificationPosition,
      );

      dispatch(
        updateTariffVerificationValues(widgetId, productId, {
          tariffTypeId,
          values: currentTariff.verificationValues,
        }),
      );
    },
    [tariffTypeId, dispatch, widgetId, productId],
  );

  return {
    handleValueChange,
  };
};
